export interface SetCookieOptions {
  path?: string;
}

/**
 * A simple abstraction for cookie interaction specific to this library. Mostly
 * used to facilitate testing.
 */
export interface Cookies {
  setCookie(name: string, value: string, options?: SetCookieOptions): void;
}

/**
 * An implementation of Cookies that interacts with the browser's real cookies.
 */
export class DocumentCookies implements Cookies {
  private readonly document: Document | null;

  constructor(document?: Document) {
    if (document) {
      this.document = document;
    } else if (typeof window !== 'undefined') {
      this.document = window.document;
    } else {
      // if the Document is unavailable this is still instantiated, but the methods will likely not
      // work...this is intentional to facilitate not having this blow up during testing, and with
      // the understanding that browsers will always have the Document
      this.document = null;
    }
  }

  private safeDocument() {
    if (!this.document) {
      throw new Error('Missing window.document.');
    }
    return this.document;
  }

  /**
   * Essentially document.cookie="name=value", but wrapped in the Cookies
   * abstraction.
   *
   * @param name The name of the cookie to set.
   * @param value The string value to set the cookie to.
   * @param options Optional overrides for default behavior like setting a
   *                cookie's path.
   */
  setCookie(name: string, value: string, options: SetCookieOptions = {}) {
    let cookie = `${name}=${value}`;
    if (options.path) {
      cookie += `; path=${options.path}`;
    }
    this.safeDocument().cookie = cookie;
  }
}
